import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../../lib/gameSounds';

interface BattleshipShip {
  size: number;
  row: number;
  col: number;
  horizontal: boolean;
  hits: number;
}

interface BattleshipLastShot {
  viewer: string;
  coord: string;
  hit: boolean;
  sunk: boolean;
  ts: number;
}

interface BattleshipState {
  size: number;
  ships: BattleshipShip[];
  shots: Array<{ row: number; col: number; hit: boolean; viewer: string }>;
  scores: Record<string, number>;
  finished: boolean;
  lastShot?: BattleshipLastShot | null;
}

/**
 * Battleship overlay — redesigned to match the gameshow shell shared with
 * Hype Million / Chat Words / Hangman / Crossword. Sapphire/blue palette
 * (naval, ocean depths) so the five games read as a series in distinct
 * colors: gold / cyan / violet / emerald / sapphire.
 */

const PALETTE = {
  bg: '#020817',
  bgInner: '#0c1e3b',
  accent: '#3b82f6', // blue-500
  accentLight: '#93c5fd', // blue-300
  accentDeep: '#1e3a8a', // blue-900
  accentBorder: 'rgba(59,130,246,0.55)',
  accentBorderSoft: 'rgba(59,130,246,0.32)',
  accentGlow: 'rgba(59,130,246,0.45)',
  green: '#22c55e',
  red: '#ef4444',
  amber: '#fbbf24',
  orange: '#f97316',
  text: '#fff',
  textDim: 'rgba(255,255,255,0.65)',
};

const ANIMATIONS = `
@keyframes bs-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes bs-spotlight { 0% { opacity: 0.3 } 50% { opacity: 0.55 } 100% { opacity: 0.3 } }
@keyframes bs-pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes bs-rise { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes bs-shot-pop { 0% { transform: scale(0.4); opacity: 0 } 70% { transform: scale(1.15); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
@keyframes bs-flash { 0% { box-shadow: 0 0 0 0 currentColor } 50% { box-shadow: 0 0 0 12px transparent } 100% { box-shadow: 0 0 0 0 transparent } }
@keyframes bs-stamp-in { 0% { transform: translate(-50%, -50%) scale(0.4) rotate(-12deg); opacity: 0 } 70% { transform: translate(-50%, -50%) scale(1.15) rotate(4deg); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1 } }
@keyframes bs-stamp-out { 0% { opacity: 1 } 100% { opacity: 0; transform: translate(-50%, -50%) scale(1.15) } }
@keyframes bs-end-pop { 0% { transform: scale(0.85); opacity: 0 } 60% { transform: scale(1.04); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
`;

export function BattleshipBody({ state }: { state: BattleshipState; config: unknown }) {
  const top = topScores(state.scores, 5);
  const totalShipCells = state.ships.reduce((acc, s) => acc + s.size, 0);
  const hits = state.ships.reduce((acc, s) => acc + s.hits, 0);
  const sunkShips = state.ships.filter((s) => s.hits >= s.size).length;
  const lastShot = state.shots[state.shots.length - 1];

  // Sound triggers
  const prevShotsRef = useRef<number>(state.shots.length);
  const prevSunkRef = useRef<number>(sunkShips);
  const prevFinishedRef = useRef<boolean>(false);
  const prevStampTsRef = useRef<number>(0);
  const [shotStamp, setShotStamp] = useState<BattleshipLastShot | null>(null);

  useEffect(() => {
    if (state.shots.length > prevShotsRef.current) {
      const ls = state.shots[state.shots.length - 1];
      void gameSounds.play(ls?.hit ? 'correct' : 'incorrect');
    }
    prevShotsRef.current = state.shots.length;
  }, [state.shots]);
  useEffect(() => {
    if (sunkShips > prevSunkRef.current) {
      void gameSounds.play('reveal');
    }
    prevSunkRef.current = sunkShips;
  }, [sunkShips]);
  useEffect(() => {
    if (state.finished && !prevFinishedRef.current) {
      void gameSounds.play('winner');
      void gameSounds.play('celebration');
    }
    prevFinishedRef.current = state.finished;
  }, [state.finished]);
  useEffect(() => {
    const ls = state.lastShot;
    if (!ls) return;
    if (ls.ts <= prevStampTsRef.current) return;
    prevStampTsRef.current = ls.ts;
    setShotStamp(ls);
    const t = setTimeout(() => setShotStamp(null), 1500);
    return () => clearTimeout(t);
  }, [state.lastShot]);

  if (state.finished) {
    return <Finished top={top} totalShips={state.ships.length} totalCells={totalShipCells} />;
  }

  const cols = 'ABCDEFGHIJ'.slice(0, state.size).split('');

  return (
    <div style={stageStyle}>
      <style>{ANIMATIONS}</style>
      <Spotlight />

      <Header
        sunkShips={sunkShips}
        totalShips={state.ships.length}
        hits={hits}
        totalCells={totalShipCells}
        shotsFired={state.shots.length}
        leader={top[0] ?? null}
      />

      <main style={mainStyle}>
        <CommandStripe />
        <div style={bodyRowStyle}>
          <BoardPanel
            size={state.size}
            cols={cols}
            shots={state.shots}
            ships={state.ships}
            lastShot={lastShot ?? null}
          />
          <SideRail
            sunkShips={sunkShips}
            totalShips={state.ships.length}
            hits={hits}
            totalCells={totalShipCells}
            shotsFired={state.shots.length}
            lastShot={lastShot ?? null}
            cols={cols}
            top={top}
          />
        </div>
      </main>

      {shotStamp && <ShotStamp event={shotStamp} />}
    </div>
  );
}

/* ───────────────────── stage / shell ───────────────────── */

const stageStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 22,
  padding: '28px 36px',
  background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
  color: PALETTE.text,
  fontFamily:
    '"Rajdhani","Orbitron",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  overflow: 'hidden',
  animation: 'bs-fadeIn 400ms ease both',
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minHeight: 0,
};

const bodyRowStyle: React.CSSProperties = {
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '1fr 320px',
  gap: 24,
  minHeight: 0,
};

function Spotlight() {
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(59,130,246,0.20) 0%, transparent 55%)',
          animation: 'bs-spotlight 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 100% 100%, rgba(30,58,138,0.18) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

/* ───────────────────── header ───────────────────── */

function Header({
  sunkShips,
  totalShips,
  hits,
  totalCells,
  shotsFired,
  leader,
}: {
  sunkShips: number;
  totalShips: number;
  hits: number;
  totalCells: number;
  shotsFired: number;
  leader: [string, number] | null;
}) {
  return (
    <header
      style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        animation: 'bs-rise 500ms ease both',
      }}
    >
      <Brand />
      <ChipRow
        sunkShips={sunkShips}
        totalShips={totalShips}
        hits={hits}
        totalCells={totalCells}
        shotsFired={shotsFired}
        leader={leader}
      />
      <ShipsRemainingRing sunkShips={sunkShips} totalShips={totalShips} />
    </header>
  );
}

function Brand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${PALETTE.accent}, ${PALETTE.accentDeep})`,
          display: 'grid',
          placeItems: 'center',
          fontSize: 32,
          color: '#fff',
          fontWeight: 900,
          fontFamily: '"Orbitron",sans-serif',
          boxShadow: `0 8px 28px ${PALETTE.accentGlow}`,
        }}
      >
        ⚓
      </div>
      <div>
        <div
          style={{
            fontSize: 12,
            letterSpacing: 4,
            color: PALETTE.accentLight,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          The
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: 3,
            lineHeight: 1,
            fontFamily: '"Orbitron","Rajdhani",sans-serif',
          }}
        >
          CHATTLE SHIP
        </div>
      </div>
    </div>
  );
}

function ChipRow({
  sunkShips,
  totalShips,
  hits,
  totalCells,
  shotsFired,
  leader,
}: {
  sunkShips: number;
  totalShips: number;
  hits: number;
  totalCells: number;
  shotsFired: number;
  leader: [string, number] | null;
}) {
  return (
    <div
      style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}
    >
      <Chip label="Sunk" value={`${sunkShips} / ${totalShips}`} highlight />
      <Chip label="Hits" value={`${hits} / ${totalCells}`} />
      <Chip label="Shots" value={String(shotsFired)} />
      {leader && <Chip label="Leader" value={`${leader[0]} · ${leader[1]}`} />}
    </div>
  );
}

function Chip({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        padding: '10px 16px',
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${highlight ? PALETTE.accentBorder : PALETTE.accentBorderSoft}`,
        borderRadius: 999,
        backdropFilter: 'blur(8px)',
        boxShadow: highlight ? `0 0 24px -8px ${PALETTE.accentGlow}` : undefined,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: highlight ? PALETTE.accentLight : PALETTE.text,
          fontFamily: '"JetBrains Mono","Orbitron",monospace',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ShipsRemainingRing({ sunkShips, totalShips }: { sunkShips: number; totalShips: number }) {
  const remaining = Math.max(0, totalShips - sunkShips);
  const pct = totalShips > 0 ? remaining / totalShips : 0;
  const urgent = remaining === 1;
  const r = 32;
  const c = 2 * Math.PI * r;
  return (
    <div
      style={{
        position: 'relative',
        width: 92,
        height: 92,
        display: 'grid',
        placeItems: 'center',
        animation: urgent ? 'bs-pulse 600ms ease-in-out infinite' : undefined,
      }}
    >
      <svg
        width={92}
        height={92}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle cx={46} cy={46} r={r} stroke="rgba(255,255,255,0.10)" strokeWidth={5} fill="none" />
        <circle
          cx={46}
          cy={46}
          r={r}
          stroke={urgent ? PALETTE.amber : PALETTE.accent}
          strokeWidth={5}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 500ms linear' }}
        />
      </svg>
      <div
        style={{
          fontSize: 22,
          fontWeight: 900,
          color: urgent ? PALETTE.amber : PALETTE.accentLight,
          fontFamily: '"Orbitron",sans-serif',
        }}
      >
        {remaining}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: -16,
          fontSize: 9,
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        SHIPS
      </div>
    </div>
  );
}

/* ───────────────────── command stripe ───────────────────── */

function CommandStripe() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '12px 18px',
        background:
          'linear-gradient(90deg, rgba(59,130,246,0.10), rgba(59,130,246,0.18), rgba(59,130,246,0.10))',
        border: `1px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 14,
        fontSize: 16,
        fontWeight: 600,
        color: PALETTE.textDim,
      }}
    >
      <span style={{ color: PALETTE.text }}>Just type a coord like</span>
      <span
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          padding: '4px 10px',
          background: 'rgba(34,197,94,0.18)',
          border: `1px solid ${PALETTE.green}`,
          borderRadius: 8,
          color: '#6ee7b7',
          fontWeight: 800,
          letterSpacing: 1,
        }}
      >
        B5
      </span>
      <span>in chat to fire</span>
    </div>
  );
}

/* ───────────────────── board ───────────────────── */

function BoardPanel({
  size,
  cols,
  shots,
  ships,
  lastShot,
}: {
  size: number;
  cols: string[];
  shots: BattleshipState['shots'];
  ships: BattleshipShip[];
  lastShot: BattleshipState['shots'][number] | null;
}) {
  const isLastShot = (r: number, c: number) => lastShot?.row === r && lastShot?.col === c;
  return (
    <div
      style={{
        position: 'relative',
        padding: 24,
        background: 'linear-gradient(180deg, rgba(12,30,59,0.88), rgba(2,8,23,0.95))',
        border: `2px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 18,
        boxShadow: '0 18px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        display: 'grid',
        placeItems: 'center',
        animation: 'bs-rise 500ms ease both',
        minHeight: 0,
      }}
    >
      {/* Subtle wave watermark behind grid */}
      <svg
        viewBox="0 0 200 80"
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0.08,
          pointerEvents: 'none',
        }}
      >
        <path
          d="M 0 40 Q 25 24, 50 40 T 100 40 T 150 40 T 200 40"
          stroke={PALETTE.accentLight}
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M 0 60 Q 25 44, 50 60 T 100 60 T 150 60 T 200 60"
          stroke={PALETTE.accentLight}
          strokeWidth="2"
          fill="none"
        />
      </svg>

      <div
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `42px repeat(${size}, minmax(36px, 58px))`,
          gap: 4,
        }}
      >
        <span />
        {cols.map((c) => (
          <Axis key={c} label={c} />
        ))}
        {Array.from({ length: size }).map((_, r) => (
          <Row key={r} row={r} size={size} shots={shots} ships={ships} isLastShot={isLastShot} />
        ))}
      </div>
    </div>
  );
}

function Axis({ label }: { label: string | number }) {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        fontFamily: '"JetBrains Mono",monospace',
        fontSize: 12,
        fontWeight: 900,
        color: PALETTE.accentLight,
        letterSpacing: 1,
      }}
    >
      {label}
    </div>
  );
}

function Row({
  row,
  size,
  shots,
  ships,
  isLastShot,
}: {
  row: number;
  size: number;
  shots: BattleshipState['shots'];
  ships: BattleshipShip[];
  isLastShot: (r: number, c: number) => boolean;
}) {
  return (
    <>
      <Axis label={row + 1} />
      {Array.from({ length: size }).map((_, col) => {
        const shot = shots.find((s) => s.row === row && s.col === col);
        const ship = shipAt(ships, row, col);
        const sunk = ship && ship.hits >= ship.size;
        const flash = isLastShot(row, col);
        return <Cell key={col} shot={shot} sunk={!!sunk} flash={flash} />;
      })}
    </>
  );
}

function Cell({
  shot,
  sunk,
  flash,
}: {
  shot: BattleshipState['shots'][number] | undefined;
  sunk: boolean;
  flash: boolean;
}) {
  const isHit = shot?.hit;
  const isMiss = shot && !shot.hit;
  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        display: 'grid',
        placeItems: 'center',
        borderRadius: 6,
        background: sunk
          ? 'linear-gradient(180deg, rgba(239,68,68,0.45), rgba(127,29,29,0.55))'
          : isHit
            ? 'linear-gradient(180deg, rgba(251,191,36,0.40), rgba(217,119,6,0.50))'
            : isMiss
              ? 'rgba(255,255,255,0.04)'
              : `rgba(59,130,246,0.18)`,
        border: sunk
          ? `2px solid ${PALETTE.red}`
          : isHit
            ? `2px solid ${PALETTE.amber}`
            : isMiss
              ? '1px solid rgba(255,255,255,0.10)'
              : `1px solid ${PALETTE.accentBorderSoft}`,
        boxShadow: sunk
          ? `0 0 24px -6px ${PALETTE.red}`
          : isHit
            ? `0 0 24px -6px ${PALETTE.amber}`
            : undefined,
        animation: flash ? 'bs-shot-pop 0.4s cubic-bezier(0.16,1,0.3,1) both' : undefined,
        color: PALETTE.red,
        transition: 'background 200ms, border-color 200ms, box-shadow 200ms',
      }}
    >
      {isHit && (
        <svg viewBox="0 0 40 40" style={{ width: '70%', height: '70%' }} aria-hidden>
          <circle cx="20" cy="20" r="14" fill={sunk ? PALETTE.red : PALETTE.amber} opacity="0.35" />
          <path
            d="M 12 12 L 28 28 M 28 12 L 12 28"
            stroke={sunk ? '#fff' : '#fff8e7'}
            strokeWidth="3.5"
            strokeLinecap="round"
          />
        </svg>
      )}
      {isMiss && (
        <svg viewBox="0 0 40 40" style={{ width: '60%', height: '60%' }} aria-hidden>
          <circle cx="20" cy="20" r="6" fill="none" stroke="white" strokeWidth="2" opacity="0.7" />
          <circle
            cx="20"
            cy="20"
            r="11"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            opacity="0.4"
          />
          <circle cx="20" cy="20" r="16" fill="none" stroke="white" strokeWidth="1" opacity="0.2" />
        </svg>
      )}
    </div>
  );
}

function shipAt(ships: BattleshipShip[], row: number, col: number): BattleshipShip | null {
  for (const ship of ships) {
    for (let i = 0; i < ship.size; i++) {
      const r = ship.horizontal ? ship.row : ship.row + i;
      const c = ship.horizontal ? ship.col + i : ship.col;
      if (r === row && c === col) return ship;
    }
  }
  return null;
}

/* ───────────────────── side rail ───────────────────── */

function SideRail({
  sunkShips,
  totalShips,
  hits,
  totalCells,
  shotsFired,
  lastShot,
  cols,
  top,
}: {
  sunkShips: number;
  totalShips: number;
  hits: number;
  totalCells: number;
  shotsFired: number;
  lastShot: BattleshipState['shots'][number] | null;
  cols: string[];
  top: Array<[string, number]>;
}) {
  const accuracy = shotsFired > 0 ? Math.round((hits / shotsFired) * 100) : 0;
  return (
    <aside
      style={{
        position: 'relative',
        zIndex: 1,
        padding: '20px 18px',
        background: 'linear-gradient(180deg, rgba(12,30,59,0.88), rgba(2,8,23,0.95))',
        border: `2px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 22,
        boxShadow: '0 20px 56px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        animation: 'bs-rise 500ms ease both',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          fontSize: 12,
          letterSpacing: 4,
          color: PALETTE.accentLight,
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 800,
        }}
      >
        Battle Stats
      </div>

      <StatRow label="Ships Sunk" value={`${sunkShips} / ${totalShips}`} />
      <StatRow label="Hits" value={`${hits} / ${totalCells}`} />
      <StatRow label="Shots Fired" value={String(shotsFired)} />
      <StatRow label="Accuracy" value={`${accuracy}%`} />

      {/* Last shot panel */}
      {lastShot && (
        <div
          style={{
            marginTop: 4,
            padding: '14px 16px',
            borderRadius: 12,
            background: lastShot.hit
              ? 'linear-gradient(135deg, rgba(251,191,36,0.22), rgba(217,119,6,0.32))'
              : 'rgba(255,255,255,0.04)',
            border: lastShot.hit
              ? `1.5px solid ${PALETTE.amber}`
              : '1px solid rgba(255,255,255,0.10)',
            boxShadow: lastShot.hit ? `0 0 28px -8px ${PALETTE.amber}` : undefined,
          }}
        >
          <div
            style={{
              fontSize: 10,
              letterSpacing: 3,
              color: PALETTE.textDim,
              fontFamily: '"Orbitron",sans-serif',
            }}
          >
            LAST SHOT
          </div>
          <div
            style={{
              fontFamily: '"Orbitron",sans-serif',
              fontSize: 28,
              fontWeight: 900,
              color: lastShot.hit ? PALETTE.amber : 'rgba(255,255,255,0.55)',
              letterSpacing: 2,
              marginTop: 2,
            }}
          >
            {lastShot.hit ? 'HIT' : 'MISS'}
          </div>
          <div style={{ fontSize: 12, color: PALETTE.textDim, marginTop: 2 }}>
            {lastShot.viewer}{' '}
            <span
              style={{
                fontFamily: '"JetBrains Mono",monospace',
                color: PALETTE.accentLight,
                fontWeight: 800,
              }}
            >
              · {cols[lastShot.col]}
              {lastShot.row + 1}
            </span>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div style={{ marginTop: 4 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 3,
            color: PALETTE.accentLight,
            fontFamily: '"Orbitron",sans-serif',
            fontWeight: 800,
            marginBottom: 8,
          }}
        >
          TOP GUNNERS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {top.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: 'rgba(255,255,255,0.35)',
                fontStyle: 'italic',
                textAlign: 'center',
                padding: 8,
              }}
            >
              No hits yet
            </div>
          ) : (
            top.map(([name, score], i) => (
              <div
                key={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  borderRadius: 10,
                  background:
                    i === 0
                      ? 'linear-gradient(90deg, rgba(59,130,246,0.30), rgba(255,255,255,0.04))'
                      : 'rgba(255,255,255,0.03)',
                  border: i === 0 ? `1px solid ${PALETTE.accent}` : '1px solid transparent',
                  boxShadow: i === 0 ? `0 0 22px -10px ${PALETTE.accentGlow}` : undefined,
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    fontFamily: '"JetBrains Mono",monospace',
                    fontWeight: 900,
                    color: PALETTE.accentLight,
                    minWidth: 18,
                  }}
                >
                  #{i + 1}
                </span>
                <span style={{ flex: 1, color: PALETTE.text }}>{name}</span>
                <span
                  style={{
                    fontFamily: '"JetBrains Mono",monospace',
                    fontWeight: 900,
                    color: PALETTE.text,
                  }}
                >
                  {score}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </aside>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <div
        style={{
          fontSize: 10,
          letterSpacing: 2,
          color: PALETTE.textDim,
          fontFamily: '"Orbitron",sans-serif',
        }}
      >
        {label.toUpperCase()}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: PALETTE.text,
          fontFamily: '"JetBrains Mono","Orbitron",monospace',
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ───────────────────── shot stamp overlay ───────────────────── */

function ShotStamp({ event }: { event: BattleshipLastShot }) {
  const sunk = event.sunk;
  const hit = event.hit;
  const color = sunk ? PALETTE.red : hit ? PALETTE.amber : PALETTE.accentLight;
  const label = sunk ? 'SUNK' : hit ? 'HIT' : 'MISS';
  return (
    <div
      key={event.ts}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 6,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        pointerEvents: 'none',
        animation:
          'bs-stamp-in 0.35s cubic-bezier(0.16,1,0.3,1) both, bs-stamp-out 0.35s ease-in 1.15s both',
      }}
    >
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 140,
          fontWeight: 900,
          letterSpacing: 6,
          color,
          textShadow: `0 0 28px ${color}, 0 0 70px ${color}66, 0 6px 18px rgba(0,0,0,0.7)`,
          WebkitTextStroke: '3px white',
        }}
      >
        {label}
      </div>
      <div
        style={{
          textAlign: 'center',
          background: 'rgba(0, 0, 0, 0.78)',
          padding: '6px 18px',
          borderRadius: 8,
          border: `2px solid ${color}`,
          boxShadow: `0 0 24px ${color}66`,
        }}
      >
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 2,
            color: 'white',
          }}
        >
          {event.coord}
        </div>
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            color: 'rgba(255,255,255,0.75)',
          }}
        >
          {event.viewer}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────── finished ───────────────────── */

function Finished({
  top,
  totalShips,
  totalCells,
}: {
  top: Array<[string, number]>;
  totalShips: number;
  totalCells: number;
}) {
  void totalCells;
  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        padding: 40,
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
        color: PALETTE.text,
        fontFamily: '"Rajdhani","Orbitron",sans-serif',
      }}
    >
      <style>{ANIMATIONS}</style>
      <div
        style={{
          padding: '48px 60px',
          borderRadius: 24,
          background: 'rgba(0,0,0,0.4)',
          border: `2px solid ${PALETTE.accentBorder}`,
          boxShadow: `0 0 100px -24px ${PALETTE.accentGlow}`,
          textAlign: 'center',
          minWidth: 520,
          animation: 'bs-end-pop 600ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 12 }}>🏆</div>
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 14,
            letterSpacing: 4,
            color: PALETTE.accentLight,
            textTransform: 'uppercase',
            fontWeight: 800,
          }}
        >
          Fleet eliminated
        </div>
        <div
          style={{
            marginTop: 12,
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 48,
            fontWeight: 900,
            color: PALETTE.text,
            textShadow: `0 0 28px ${PALETTE.accentGlow}`,
          }}
        >
          {totalShips} <span style={{ color: PALETTE.textDim, fontSize: 32 }}>ships sunk</span>
        </div>
        {top.length > 0 && (
          <div
            style={{
              marginTop: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              minWidth: 360,
            }}
          >
            {top.map(([name, score], i) => (
              <div
                key={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '10px 18px',
                  borderRadius: 10,
                  background:
                    i === 0
                      ? `linear-gradient(90deg, ${PALETTE.accentBorder}, rgba(255,255,255,0.04))`
                      : 'rgba(255,255,255,0.04)',
                  border: i === 0 ? `1px solid ${PALETTE.accent}` : '1px solid transparent',
                  boxShadow: i === 0 ? `0 0 26px -10px ${PALETTE.accentGlow}` : undefined,
                  fontSize: 16,
                }}
              >
                <span
                  style={{
                    fontFamily: '"JetBrains Mono",monospace',
                    fontWeight: 900,
                    color: PALETTE.accentLight,
                    minWidth: 32,
                  }}
                >
                  #{i + 1}
                </span>
                <span style={{ flex: 1, color: PALETTE.text }}>{name}</span>
                <span
                  style={{
                    fontFamily: '"JetBrains Mono",monospace',
                    fontWeight: 900,
                    color: PALETTE.text,
                  }}
                >
                  {score}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────── helpers ───────────────────── */

function topScores(scores: Record<string, number>, n: number): Array<[string, number]> {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}
